import { thunk } from 'redux-thunk';
import { createListenerMiddleware, configureStore } from '@reduxjs/toolkit';
import rootReducer from '@/store/reducers/root';

// Middlewares
import analyticsMiddleware from './middleware/googleAnalyticsReduxEventTracking';
import {
  addRosterListenerMiddleware,
  addEntitlementListenerMiddleware,
  addDashboardListenerMiddleware,
  addStudentAssignmentsFetchListenerMiddleware,
  addTeacherAssignmentsFetchListenerMiddleware,
  addCurriculumFetchListenerMiddleware,
  addCreateLessonsListenerMiddleware,
  addUpdateResourcesQueueListenerMiddleware,
  addUpdateMasteryQueueListenerMiddleware,
  addFetchResourceQueueListenerMiddleware,
  updateLessonResourcesMiddleware,
  addBatchCreateStudentAssignmentsListenerMiddleware,
  addBatchUnassignAssignmentsListenerMiddleware,
  addBatchUpdateAssignmentsListenerMiddleware,
  addCreateAssignmentListenerMiddleware,
  addWeekChangeListenerMiddleware,
  addFetchSkillsMetadataListenerMiddleware,
  addUpdateCRUDAssignmentsQueueListenerMiddleware,
  addCreateAssignmentsListenerMiddleware,
  addUpdateAssignmentsListenerMiddleware,
} from './store/middlewares';

const listenerMiddleware = createListenerMiddleware();

// Handles logic related to roster data/changes (Assuming based on name - file not read)
addRosterListenerMiddleware(listenerMiddleware);
// Handles logic related to user entitlements/permissions (Assuming based on name - file not read)
addEntitlementListenerMiddleware(listenerMiddleware);
// Handles logic related to dashboard data fetching or updates (Assuming based on name - file not read)
addDashboardListenerMiddleware(listenerMiddleware);

/* START WEEKLY PLANNER MIDDLEWARES */
// Listens for fetchTeacherAssignments.fulfilled; triggers fetchCurriculum if student assignments also fulfilled
addTeacherAssignmentsFetchListenerMiddleware(listenerMiddleware);
// Listens for fetchStudentAssignments.fulfilled; triggers fetchCurriculum if teacher assignments also fulfilled
addStudentAssignmentsFetchListenerMiddleware(listenerMiddleware);
// Listens for fetchCurriculum.fulfilled; triggers fetchDefaultStoryData or createLessons
addCurriculumFetchListenerMiddleware(listenerMiddleware);
addCreateLessonsListenerMiddleware(listenerMiddleware);
// Listens for updateResourcesQueue action; likely processes the queue to fetch actual resources (File not read)
addUpdateResourcesQueueListenerMiddleware(listenerMiddleware);
// Listens for updateSkillMasteryQueue action; likely processes the queue to fetch mastery data (File not read)
addUpdateMasteryQueueListenerMiddleware(listenerMiddleware);
// Listens for ? (likely updateResourcesQueue or fetchSkillResources.fulfilled); processes resource fetch queue/results (File not read)
addFetchResourceQueueListenerMiddleware(listenerMiddleware);
// Listens for fetchResourcesMetadata.fulfilled; updates lesson resources (File not read)
updateLessonResourcesMiddleware(listenerMiddleware);
// Listens for updateCRUDAssignmentsQueue action; starts processing the assignment CRUD queue if not already running
addUpdateCRUDAssignmentsQueueListenerMiddleware(listenerMiddleware);
// Listens for batchCreateStudentAssignments.fulfilled; processes next CRUD queue item or aggregates lessons
addBatchCreateStudentAssignmentsListenerMiddleware(listenerMiddleware);
// Listens for batchUnassignAssignments.fulfilled; processes next CRUD queue item or aggregates lessons
addBatchUnassignAssignmentsListenerMiddleware(listenerMiddleware);
// Listens for batchUpdateAssignments.fulfilled; processes next CRUD queue item or aggregates lessons
addBatchUpdateAssignmentsListenerMiddleware(listenerMiddleware);
// Listens for createAssignment.fulfilled; processes next CRUD queue item or aggregates lessons
addCreateAssignmentListenerMiddleware(listenerMiddleware);
// Listens for updateWeeklyPlanDates action; resets state and refetches assignments for the new week
addWeekChangeListenerMiddleware(listenerMiddleware);
// Listens for createLessons action; triggers fetchSkillsMetadata for skills in the new curriculum
addFetchSkillsMetadataListenerMiddleware(listenerMiddleware);
/* END WEEKLY PLANNER MIDDLEWARES */
// Listens for batchCreateStudentAssignments.fulfilled; triggers fetchCurriculum if teacher assignments also fulfilled
addCreateAssignmentsListenerMiddleware(listenerMiddleware);
// Listens for batchUnassignAssignments.fulfilled; triggers fetchCurriculum if teacher assignments also fulfilled
addUpdateAssignmentsListenerMiddleware(listenerMiddleware);

interface MiddlewareConfig {
  getDefaultMiddleware: () => any[];
}

const configureMiddleware = (getDefaultMiddleware: any) => {
  const middlewares: any[] = [
    listenerMiddleware.middleware,
  ];
  if (process.env.NODE_ENV !== 'production') {
    middlewares.push(analyticsMiddleware);
  }
  return getDefaultMiddleware().prepend(middlewares);
};

// Configure the store
const store = configureStore({
  reducer: rootReducer,
  middleware: configureMiddleware,
  devTools: process.env.NODE_ENV !== 'production',
});

export {
  store as default,
  store
};
